import React, { useMemo } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  Plus, Upload, Download, Trash2, Search, ArrowUpAZ, ArrowDownAZ,
  Store, MapPin, Edit2, Loader2, ChevronLeft, ChevronRight,
} from 'lucide-react';
import { getThumbnailUrl } from '../../lib/imageUtils';

export const AdminStallsTab = ({
  allStalls,
  allVendors = [],
  stallFilter, setStallFilter,
  stallSearch, setStallSearch,
  stallSortOrder, setStallSortOrder,
  stallsPerPage, setStallsPerPage,
  stallsCurrentPage, setStallsCurrentPage,
  setShowAddStallModal, setShowBulkUploadStalls,
  setShowExportStallsModal, setShowBulkDeleteStallsModal,
  handleDeleteSingleStall, openEditStallModal,
  processing,
}) => {
  // Build a lookup from user _id -> user, so we can determine if a stall's
  // owner has an expired subscription (trial ended, cash expired, or cancelled)
  const vendorById = useMemo(() => {
    const map = {};
    (allVendors || []).forEach(v => { if (v?._id) map[v._id] = v; });
    return map;
  }, [allVendors]);

  const isStallExpired = (stall) => {
    if (!stall) return false;
    const vendorId = stall.created_by || stall.vendorId;
    if (!vendorId) return false;
    const v = vendorById[vendorId];
    if (!v) return false;
    const now = new Date();
    // Cancelled subscriptions
    if (v.subscription_status === 'cancelled') return true;
    // Trial subscription expired (7 days from trial_start_date)
    if (v.subscription === 'trial' && v.trial_start_date) {
      const start = new Date(v.trial_start_date);
      if (!isNaN(start)) {
        const trialEnd = new Date(start.getTime() + 7 * 24 * 60 * 60 * 1000);
        if (now > trialEnd) return true;
      }
    }
    // Cash / manual subscriptions past their billing date
    if ((v.subscription === 'cash' || v.subscription === 'manual') && v.next_billing_date) {
      const nb = new Date(v.next_billing_date);
      if (!isNaN(nb) && now > nb) return true;
    }
    return false;
  };
  const filteredStalls = useMemo(() => allStalls
    .filter(stall => {
      if (stallFilter === 'all') return true;
      if (stallFilter === 'assigned') return stall.vendorId || stall.created_by;
      if (stallFilter === 'unassigned') return !stall.vendorId && !stall.created_by;
      if (stallFilter === 'published') return stall.status === 'published';
      if (stallFilter === 'draft') return !stall.status || stall.status === 'draft';
      return true;
    })
    .filter(stall => {
      if (!stallSearch) return true;
      const search = stallSearch.toLowerCase();
      return (
        (stall.stallName || '').toLowerCase().includes(search) ||
        (stall.stallNumber || '').toLowerCase().includes(search) ||
        (stall.category || '').toLowerCase().includes(search) ||
        (stall.market || '').toLowerCase().includes(search) ||
        (stall.slogan || '').toLowerCase().includes(search)
      );
    })
    .sort((a, b) => {
      const nameA = (a.stallName || '').toLowerCase();
      const nameB = (b.stallName || '').toLowerCase();
      return stallSortOrder === 'asc' ? nameA.localeCompare(nameB) : nameB.localeCompare(nameA);
    }), [allStalls, stallFilter, stallSearch, stallSortOrder]);

  const totalStalls = filteredStalls.length;

  const perPage = stallsPerPage === 'all' ? totalStalls : parseInt(stallsPerPage);
  const totalPages = Math.ceil(totalStalls / perPage) || 1;
  const startIndex = (stallsCurrentPage - 1) * perPage;
  const paginatedStalls = stallsPerPage === 'all' ? filteredStalls : filteredStalls.slice(startIndex, startIndex + perPage);

  return (
    <>
      <div className="flex flex-wrap gap-2 mb-4">
        <Button onClick={() => setShowAddStallModal(true)} className="bg-[#FFD12D] hover:bg-[#252525] text-[#252525] hover:text-[#FFD12D] text-xs sm:text-sm h-8 sm:h-9 px-2 sm:px-3"><Plus className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1 sm:mr-2" /> Add</Button>
        <Button onClick={() => setShowBulkUploadStalls(true)} className="bg-green-600 hover:bg-green-700 text-white text-xs sm:text-sm h-8 sm:h-9 px-2 sm:px-3"><Upload className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1 sm:mr-2" /> <span className="hidden sm:inline">Bulk </span>Upload</Button>
        <Button onClick={() => setShowExportStallsModal(true)} className="bg-blue-600 hover:bg-blue-700 text-white text-xs sm:text-sm h-8 sm:h-9 px-2 sm:px-3"><Download className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1 sm:mr-2" /> Export</Button>
        <Button onClick={() => setShowBulkDeleteStallsModal(true)} variant="destructive" className="text-xs sm:text-sm h-8 sm:h-9 px-2 sm:px-3"><Trash2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1 sm:mr-2" /> <span className="hidden sm:inline">Bulk </span>Delete</Button>
      </div>

      <div className="flex flex-col sm:flex-row gap-2 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <Input type="text" placeholder="Search stalls..." value={stallSearch} onChange={(e) => setStallSearch(e.target.value)} className="pl-9 dark:bg-[#1a1a1a] dark:text-white" />
        </div>
        <Button variant="outline" onClick={() => setStallSortOrder(stallSortOrder === 'asc' ? 'desc' : 'asc')} className="flex items-center gap-2 border-gray-300 dark:border-gray-600">
          {stallSortOrder === 'asc' ? <><ArrowUpAZ className="w-4 h-4" /> A-Z</> : <><ArrowDownAZ className="w-4 h-4" /> Z-A</>}
        </Button>
      </div>

      <div className="flex items-center gap-1 text-xs sm:text-sm border-b border-gray-200 dark:border-gray-700 pb-3 mb-4 overflow-x-auto no-scrollbar">
        <div className="flex items-center gap-1 flex-shrink-0">
        {[
          { key: 'all', label: `All (${allStalls.length})` },
          { key: 'assigned', label: `Assigned (${allStalls.filter(s => s.vendorId || s.created_by).length})` },
          { key: 'unassigned', label: `Unassigned (${allStalls.filter(s => !s.vendorId && !s.created_by).length})` },
          { key: 'published', label: `Published (${allStalls.filter(s => s.status === 'published').length})` },
          { key: 'draft', label: `Draft (${allStalls.filter(s => !s.status || s.status === 'draft').length})` },
        ].map(({ key, label }, i, arr) => (
          <React.Fragment key={key}>
            <button onClick={() => { setStallFilter(key); setStallsCurrentPage(1); }}
              className={`whitespace-nowrap hover:text-[#FFD12D] transition-colors ${stallFilter === key ? 'text-[#FFD12D] font-semibold' : 'text-gray-600 dark:text-gray-400'}`}>
              {label}
            </button>
            {i < arr.length - 1 && <span className="text-gray-400 dark:text-gray-600">|</span>}
          </React.Fragment>
        ))}
        </div>
        <div className="ml-auto flex items-center gap-2 flex-shrink-0">
          <span className="text-gray-600 dark:text-gray-400 text-xs sm:text-sm">Show:</span>
          <Select value={stallsPerPage} onValueChange={(value) => { setStallsPerPage(value); setStallsCurrentPage(1); }}>
            <SelectTrigger className="w-20 sm:w-24 h-8 text-xs sm:text-sm dark:bg-[#1a1a1a] dark:text-white dark:border-gray-600"><SelectValue /></SelectTrigger>
            <SelectContent className="dark:bg-[#1a1a1a] dark:border-gray-700">
              {['10', '20', '50', '100', 'all'].map(v => <SelectItem key={v} value={v} className="dark:text-white">{v === 'all' ? 'All' : v}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {totalStalls > 0 && stallsPerPage !== 'all' && (
        <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-400 mb-3">
          <span>Showing {startIndex + 1} - {Math.min(startIndex + perPage, totalStalls)} of {totalStalls}</span>
          {totalPages > 1 && <span>Page {stallsCurrentPage} of {totalPages}</span>}
        </div>
      )}

      {paginatedStalls.length === 0 ? (
        <div className="text-center py-12 text-gray-500 dark:text-gray-400">
          <Store className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>No {stallFilter === 'all' ? 'stalls' : stallFilter + ' stalls'} found{stallSearch ? ` matching "${stallSearch}"` : ''}</p>
        </div>
      ) : (
        <>
          <div className="space-y-2 sm:space-y-3">
          {paginatedStalls.map((stall) => (
            <div key={stall._id} className="p-3 sm:p-4 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-[#252525] hover:border-[#FFD12D] transition-all mb-2 sm:mb-0">
              <div className="flex items-start gap-3 sm:gap-4">
                <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-lg overflow-hidden flex-shrink-0 bg-gray-100 dark:bg-gray-800">
                  {stall.logo ? (
                    <img src={getThumbnailUrl(stall.logo)} alt={stall.stallName} className="w-full h-full object-cover" loading="lazy" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center"><Store className="w-6 h-6 sm:w-8 sm:h-8 text-gray-400" /></div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-bold text-sm sm:text-lg text-[#252525] dark:text-white truncate">{stall.stallName}</h3>
                  <div className="flex items-center gap-1 sm:gap-2 mt-0.5 sm:mt-1 flex-wrap">
                    <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">#{stall.stallNumber}</span>
                    <span className="text-gray-400 text-xs">&bull;</span>
                    <span className="text-xs sm:text-sm font-semibold text-[#FFD12D]">{stall.category}</span>
                  </div>
                  <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                    <span className={`px-1.5 sm:px-2 py-0.5 text-[10px] sm:text-xs rounded-full font-semibold ${stall.status === 'published' ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300' : 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300'}`}>
                      {stall.status === 'published' ? '\u2713 Published' : '\u25CB Draft'}
                    </span>
                    <span className={`px-1.5 sm:px-2 py-0.5 text-[10px] sm:text-xs rounded-full font-semibold ${stall.vendorId || stall.created_by ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300' : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'}`}>
                      {stall.vendorId || stall.created_by ? '\uD83D\uDC64 Assigned' : '\u26A0 Unassigned'}
                    </span>
                    {(!stall.status || stall.status === 'draft') && isStallExpired(stall) && (
                      <span
                        className="px-1.5 sm:px-2 py-0.5 text-[10px] sm:text-xs rounded-full font-semibold bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300"
                        data-testid={`stall-expired-${stall._id}`}
                      >
                        ⏰ Expired
                      </span>
                    )}
                  </div>
                  {stall.location?.lat && stall.location?.lng && (
                    <p className="text-[10px] sm:text-xs text-gray-500 dark:text-gray-400 mt-1 flex items-center gap-1">
                      <MapPin className="w-3 h-3 flex-shrink-0" /> {stall.location.lat.toFixed(4)}, {stall.location.lng.toFixed(4)}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 mt-2 sm:mt-3 pt-2 sm:pt-0 border-t sm:border-t-0 border-gray-100 dark:border-gray-700">
                <Button onClick={() => handleDeleteSingleStall(stall._id, stall.stallName)} disabled={processing === `delete-stall-${stall._id}`} variant="destructive" className="flex-1 sm:flex-none h-8 text-xs sm:text-sm flex items-center justify-center gap-1 sm:gap-2" data-testid={`delete-stall-${stall._id}`}>
                  {processing === `delete-stall-${stall._id}` ? <Loader2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 animate-spin" /> : <Trash2 className="w-3.5 h-3.5 sm:w-4 sm:h-4" />} Delete
                </Button>
                <Button onClick={() => openEditStallModal(stall)} className="flex-1 sm:flex-none h-8 text-xs sm:text-sm bg-[#FFD12D] hover:bg-[#252525] text-[#252525] hover:text-[#FFD12D] flex items-center justify-center gap-1 sm:gap-2" data-testid={`edit-stall-${stall._id}`}>
                  <Edit2 className="w-3.5 h-3.5 sm:w-4 sm:h-4" /> Edit
                </Button>
              </div>
            </div>
          ))}
          </div>
          {totalPages > 1 && stallsPerPage !== 'all' && (
            <div className="flex items-center justify-center gap-2 mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
              <Button onClick={() => setStallsCurrentPage(1)} disabled={stallsCurrentPage === 1} variant="outline" size="sm" className="dark:border-gray-600 dark:text-white disabled:opacity-50">First</Button>
              <Button onClick={() => setStallsCurrentPage(p => Math.max(1, p - 1))} disabled={stallsCurrentPage === 1} variant="outline" size="sm" className="dark:border-gray-600 dark:text-white disabled:opacity-50"><ChevronLeft className="w-4 h-4" /></Button>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) pageNum = i + 1;
                else if (stallsCurrentPage <= 3) pageNum = i + 1;
                else if (stallsCurrentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
                else pageNum = stallsCurrentPage - 2 + i;
                return (
                  <Button key={pageNum} onClick={() => setStallsCurrentPage(pageNum)} variant={stallsCurrentPage === pageNum ? "default" : "outline"} size="sm"
                    className={stallsCurrentPage === pageNum ? "bg-[#FFD12D] text-[#252525] hover:bg-[#252525] hover:text-[#FFD12D]" : "dark:border-gray-600 dark:text-white"}>
                    {pageNum}
                  </Button>
                );
              })}
              <Button onClick={() => setStallsCurrentPage(p => Math.min(totalPages, p + 1))} disabled={stallsCurrentPage === totalPages} variant="outline" size="sm" className="dark:border-gray-600 dark:text-white disabled:opacity-50"><ChevronRight className="w-4 h-4" /></Button>
              <Button onClick={() => setStallsCurrentPage(totalPages)} disabled={stallsCurrentPage === totalPages} variant="outline" size="sm" className="dark:border-gray-600 dark:text-white disabled:opacity-50">Last</Button>
            </div>
          )}
        </>
      )}
    </>
  );
};
